import React, { useRef } from "react";
import { View, Text, StyleSheet, FlatList, Alert, RefreshControl } from "react-native";
import { Image } from "expo-image";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { spacing, typography } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { Feather } from "@expo/vector-icons";
import type { Chat } from "../../types/chat";
import type { RootStackParamList } from "../../types/navigation";
import { useAuth } from "../../hooks/useAuth";
import { useChatActions } from "../../hooks/useChatActions";
import { useChatReadStatus } from "../../hooks/useChatReadStatus";
import { Swipeable, TouchableOpacity } from "react-native-gesture-handler";
import { supabase } from "../../lib/supabase";
import { useQuery } from "@tanstack/react-query";
import { platformAlert } from "../../utils/platformAlert";

interface ChatListProps {
  chats: Chat[];
  customRightActions?: (chat: Chat) => React.ReactNode;
  customSwipeableRefs?: React.MutableRefObject<{
    [key: string]: Swipeable | null;
  }>;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

type ChatScreenNavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  "Chat" | "Vendor"
>;

export function ChatList({
  chats,
  customRightActions,
  customSwipeableRefs,
  onRefresh,
  isRefreshing = false,
}: ChatListProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<ChatScreenNavigationProp>();
  const { user } = useAuth();
  const { archiveChat } = useChatActions();
  const { readStatus } = useChatReadStatus();
  const swipeableRefs = useRef<{ [key: string]: Swipeable | null }>({});

  // Fetch the current user's vendor ID if they have one
  const { data: currentUserVendor } = useQuery({
    queryKey: ["current-user-vendor", user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("vendors")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (error || !data) return null;
      return data.id;
    },
    enabled: !!user,
  });

  // Process chats to ensure we show the correct vendor and filter out self-chats (optimized)
  const processedChats = React.useMemo(() => {
    if (!chats || !user) return [];

    return chats
      .filter((chat) => {
        // Filter out definite self-chats only (exactly 1 participant who is the current user)
        // We're strict here to avoid accidentally filtering out legitimate chats
        // if participant data is incomplete
        if (
          chat.participants &&
          chat.participants.length === 1 &&
          chat.participants[0].user_id === user.id
        ) {
          console.log('[ChatList] Filtering out self-chat:', chat.id);
          return false;
        }
        return true;
      })
      .map((chat) => {
        // If this chat has a vendor and it's the current user's vendor
        if (chat.vendor && currentUserVendor === chat.vendor.id) {
          // Show the other participant instead of the vendor
          const otherParticipant = chat.participants.find(
            (p) => p.user_id !== chat.vendor?.user_id
          );
          if (otherParticipant?.user) {
            return { ...chat, vendor: undefined };
          }
        }
        return chat;
      });
  }, [chats, user?.id, currentUserVendor]);

  // Use the provided refs if available, otherwise use the component's internal refs
  const refsToUse = customSwipeableRefs || swipeableRefs;

  const handleArchive = (chat: Chat) => {
    platformAlert("Archive Chat", "Are you sure you want to archive this chat?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Archive",
        onPress: () => {
          archiveChat.mutate(chat.id);
          refsToUse.current[chat.id]?.close();
        },
        style: "destructive",
      },
    ]);
  };

  const defaultRenderRightActions = (chat: Chat) => {
    return (
      <View style={styles.rightActions}>
        <TouchableOpacity
          style={styles.archiveAction}
          onPress={() => handleArchive(chat)}
        >
          <Feather name="archive" size={24} color="#fff" />
          <Text style={styles.actionText}>Archive</Text>
        </TouchableOpacity>
      </View>
    );
  };

  // Use custom right actions if provided, otherwise use the default
  const renderRightActions = customRightActions || defaultRenderRightActions;

  const renderItem = ({ item: chat }: { item: Chat }) => {
    // Determine if this is a vendor chat
    const isVendorChat = !!chat.vendor;

    // Find the other participant (not the current user)
    const otherParticipant = chat.participants?.find(
      (p) => p.user_id !== user?.id
    );

    // Determine display information for the chat
    let displayName: string;
    let avatarUrl: string | undefined;
    let userId: string | null;

    // Enhanced display logic with proper fallbacks
    // Priority: Store name > Username > Full name > "User"
    if (isVendorChat && chat.vendor) {
      // For vendor chats, always prioritize the vendor store name
      displayName = chat.vendor.store_name;
      avatarUrl = chat.vendor.logo_image;
      userId = chat.vendor.user_id;
    } else if (otherParticipant?.user) {
      // Check if the other participant has a store - show store name if they do
      if (otherParticipant.user.vendor?.store_name) {
        displayName = otherParticipant.user.vendor.store_name;
        // Could use vendor logo if available, fallback to user avatar
        avatarUrl = otherParticipant.user.avatar_url;
      } else if (otherParticipant.user.username) {
        displayName = `@${otherParticipant.user.username}`;
        avatarUrl = otherParticipant.user.avatar_url;
      } else if (otherParticipant.user.full_name) {
        displayName = otherParticipant.user.full_name;
        avatarUrl = otherParticipant.user.avatar_url;
      } else {
        displayName = "User";
        avatarUrl = otherParticipant.user.avatar_url;
      }
      userId = otherParticipant.user_id;
    } else if (otherParticipant) {
      // Fallback for when participant exists but no user data
      displayName = "User";
      avatarUrl = undefined;
      userId = otherParticipant.user_id;
    } else {
      // Last resort fallback
      displayName = "Unknown User";
      avatarUrl = undefined;
      userId = null;
    }

    // Check if chat has unread messages - computed directly to avoid stale closures
    const hasNewMessage = (() => {
      const lastMessageTime = chat.last_message?.created_at;
      const lastMessageSenderId = chat.last_message?.sender_id;
      
      // If no last message or message is from current user, not unread
      if (!lastMessageTime || !lastMessageSenderId || lastMessageSenderId === user?.id) {
        return false;
      }

      const lastReadTime = readStatus[chat.id];
      
      // If never read this chat, it's unread
      if (!lastReadTime) {
        return true;
      }

      // Compare timestamps - unread if last message is after last read
      return new Date(lastMessageTime).getTime() > new Date(lastReadTime).getTime();
    })();

    const ChatContent = () => (
      <TouchableOpacity
        activeOpacity={0.7}
        style={styles.chatCard}
        onPress={() => navigation.navigate("Chat", { id: chat.id })}
      >
        <View style={styles.chatItemInner}>
          <View style={styles.avatarWrapper}>
            {avatarUrl ? (
              <Image 
                source={{ uri: avatarUrl }} 
                style={styles.avatar}
                contentFit="cover"
                cachePolicy="memory-disk"
                transition={200}
              />
            ) : (
              <View style={styles.avatarPlaceholder}>
                <Feather name="user" size={24} color="#FFFFFF" />
              </View>
            )}
            {hasNewMessage && <View style={styles.activeDot} />}
          </View>

          <View style={styles.chatInfo}>
            <View style={styles.nameHeader}>
              <Text style={styles.userName} numberOfLines={1}>{displayName}</Text>
              {chat.last_message && (
                <Text style={styles.timestamp}>
                  {new Date(chat.last_message.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                </Text>
              )}
            </View>
            
            <View style={styles.messageRow}>
              <Text style={[
                styles.lastMessage,
                hasNewMessage && styles.unreadMessageText
              ]} numberOfLines={1}>
                {chat.last_message ? chat.last_message.text : "No messages yet"}
              </Text>
              
              {chat.unread_count && chat.unread_count > 0 ? (
                <View style={styles.unreadBadge}>
                  <Text style={styles.unreadCount}>{chat.unread_count}</Text>
                </View>
              ) : hasNewMessage ? (
                <View style={styles.newMessageIndicator}>
                  <Text style={styles.newMessageText}>New</Text>
                </View>
              ) : (
                <Feather name="chevron-right" size={16} color={colors.textLight} />
              )}
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );

    return (
      <Swipeable
        ref={(ref) => (refsToUse.current[chat.id] = ref)}
        renderRightActions={() => renderRightActions(chat)}
        rightThreshold={40}
        overshootRight={false}
        containerStyle={styles.swipeableContainer}
        enableTrackpadTwoFingerGesture
        friction={2}
        leftThreshold={30}
        useNativeAnimations
      >
        <ChatContent />
      </Swipeable>
    );
  };

  // Sort chats by last message time, putting most recent last
  const sortedChats = [...processedChats].sort((a, b) => {
    const timeA = a.last_message?.created_at || a.created_at;
    const timeB = b.last_message?.created_at || b.created_at;
    // Reverse the sort order by swapping timeA and timeB
    return new Date(timeB).getTime() - new Date(timeA).getTime();
  });

  if (!sortedChats.length) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No conversations yet</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={sortedChats}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        contentContainerStyle={styles.listContent}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
              title="Pull to refresh chats"
            />
          ) : undefined
        }
      />
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  listContent: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xs,
    paddingBottom: spacing.xl,
  },
  chatCard: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    marginBottom: spacing.md,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
  },
  chatItemInner: {
    flexDirection: "row",
    alignItems: "center",
    padding: spacing.md,
    minHeight: 88,
  },
  avatarWrapper: {
    position: 'relative',
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 18,
    backgroundColor: colors.background,
  },
  avatarPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 18,
    backgroundColor: colors.background,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.border,
  },
  activeDot: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: colors.surface,
  },
  chatInfo: {
    flex: 1,
    marginLeft: spacing.md,
    justifyContent: 'center',
  },
  nameHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  userName: {
    fontSize: 17,
    fontWeight: "600",
    color: colors.text,
    letterSpacing: -0.2,
    flex: 1,
    marginRight: spacing.sm,
  },
  timestamp: {
    fontSize: 12,
    color: colors.textLight,
    fontWeight: '500',
  },
  messageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  lastMessage: {
    fontSize: 14,
    color: colors.textSecondary,
    flex: 1,
    marginRight: spacing.md,
  },
  unreadMessageText: {
    color: colors.text,
    fontWeight: '500',
  },
  unreadBadge: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 6,
  },
  unreadCount: {
    fontSize: 11,
    color: "#000",
    fontWeight: "700",
  },
  newMessageIndicator: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  newMessageText: {
    fontSize: 11,
    color: "#000",
    fontWeight: "700",
  },
  rightActions: {
    flexDirection: "row",
    alignItems: "stretch",
    marginBottom: spacing.md,
    borderRadius: 20,
    overflow: 'hidden',
  },
  archiveAction: {
    backgroundColor: colors.primary,
    justifyContent: "center",
    alignItems: "center",
    width: 100,
    height: '100%',
    flexDirection: "column",
    gap: spacing.xs,
  },
  actionText: {
    fontSize: 12,
    fontWeight: '600',
    color: "#000",
  },
  swipeableContainer: {
    backgroundColor: colors.background,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: spacing.xl,
  },
  emptyText: {
    fontSize: 16,
    color: colors.textLight,
    textAlign: 'center',
  },
});
